import { useMemo } from 'react';
import type { BusinessMetrics } from '../../api/types';
import { useAdminProducts, useBusinessMetrics } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { formatMoney, formatNumber } from '../../lib/format';
import {
  BarList,
  Card,
  CardHeader,
  EmptyState,
  SkeletonCard,
  StatTile,
  type BarDatum,
} from '../../ui';

/**
 * Poslovne metrike.
 *
 * Brojke se NE računaju ovdje — `GET /v1/admin/metrics` je jedino mjesto koje ih
 * zna, pa admin i eventualni izvještaji nikad ne pokažu različitu konverziju.
 *
 * Oblik prikaza (skill `dataviz`): pločice nose pojedinačne brojke, a graf samo
 * ono što se stvarno uspoređuje po veličini. Jedna serija → jedna boja iz tokena
 * (`accent`), bez legende; vrijednost stoji uz traku i u tabličnom blizancu, pa
 * se nikad ne čita samo iz boje. Nema dviju osi ni ljestvice boja po rangu.
 */
export function AdminAnalytics() {
  const { t, locale } = useTranslation();
  const query = useBusinessMetrics();
  const products = useAdminProducts();

  const productNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products.data?.items ?? []) map.set(p.id, locale === 'en' ? p.nameEn : p.nameHr);
    return map;
  }, [products.data, locale]);

  const tableLabels = {
    label: t('metrics.tableLabel'),
    value: t('metrics.tableValue'),
    showTable: t('chart.showTable'),
    hideTable: t('chart.hideTable'),
  };

  return (
    <>
      <PageHeader
        eyebrow={t('shell.admin')}
        title={t('admin.analytics')}
        subtitle={t('metrics.subtitle')}
      />
      <QueryBoundary query={query} skeleton={<SkeletonCard />}>
        {(m) => (
          <div className="flex flex-col gap-6">
            <KpiRow metrics={m} />

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader title={t('metrics.engagementTitle')} eyebrow={t('metrics.users')} />
                <p className="mb-4 text-[13px] text-ink-muted">{t('metrics.engagementHelp')}</p>
                <BarList
                  ariaLabel={t('metrics.engagementTitle')}
                  labels={tableLabels}
                  data={[
                    {
                      key: 'total',
                      label: t('metrics.usersTotal'),
                      value: m.users.total,
                      display: formatNumber(m.users.total, locale),
                    },
                    {
                      key: 'active30',
                      label: t('metrics.active30'),
                      value: m.users.active30,
                      display: formatNumber(m.users.active30, locale),
                    },
                    {
                      key: 'active7',
                      label: t('metrics.active7'),
                      value: m.users.active7,
                      display: formatNumber(m.users.active7, locale),
                    },
                  ]}
                />
              </Card>

              <Card>
                <CardHeader title={t('metrics.topProducts')} eyebrow={t('shell.products')} />
                <TopProducts metrics={m} names={productNames} labels={tableLabels} />
              </Card>
            </div>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function KpiRow({ metrics: m }: { metrics: BusinessMetrics }) {
  const { t, locale } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="metrics-kpis">
      <StatTile label={t('metrics.usersTotal')} value={formatNumber(m.users.total, locale)} />
      <StatTile label={t('admin.activeUsers')} value={formatNumber(m.users.active30, locale)} />
      <StatTile label={t('admin.conversion')} value={m.conversionPct} unit="%" />
      <StatTile
                label={t('admin.churn')}
                value={m.churnPct}
                unit="%"
                hint={t('admin.churnHint')}
              />
      <StatTile
        label={t('metrics.revenueTotal')}
        value={formatMoney(m.revenue.totalCents, m.revenue.currency, locale)}
      />
      <StatTile
        label={t('metrics.revenue30')}
        value={formatMoney(m.revenue.last30Cents, m.revenue.currency, locale)}
      />
      <StatTile
        label={t('metrics.subscriptionsActive')}
        value={formatNumber(m.subscriptions.active, locale)}
      />
      <StatTile
        label={t('metrics.canceled30')}
        value={formatNumber(m.subscriptions.canceledLast30, locale)}
      />
      <StatTile label={t('metrics.trialsActive')} value={formatNumber(m.trials.active, locale)} />
      <StatTile
        label={t('metrics.trialsConverted')}
        value={formatNumber(m.trials.converted, locale)}
      />
      <StatTile label={t('metrics.workoutsTotal')} value={formatNumber(m.workouts.total, locale)} />
      <StatTile
        label={t('metrics.workoutsPerClient')}
        value={formatNumber(m.workouts.averagePerClient, locale)}
      />
    </div>
  );
}

/**
 * Najpopularniji proizvodi. Preko sedam stavki rep ide u „ostalo” — nova boja
 * ili nova traka po proizvodu prestaje nositi informaciju.
 */
function TopProducts({
  metrics: m,
  names,
  labels,
}: {
  metrics: BusinessMetrics;
  names: Map<string, string>;
  labels: { label: string; value: string; showTable: string; hideTable: string };
}) {
  const { t, locale } = useTranslation();

  if (m.topProducts.length === 0) {
    return <EmptyState message={t('metrics.noProducts')} />;
  }

  const top = m.topProducts.slice(0, 7);
  const restCount = m.topProducts.slice(7).reduce((n, p) => n + p.count, 0);

  const data: BarDatum[] = [
    ...top.map((p) => ({
      key: p.productId,
      label: names.get(p.productId) ?? p.productId,
      value: p.count,
      display: formatNumber(p.count, locale),
    })),
    ...(restCount > 0
      ? [
          {
            key: 'other',
            label: t('metrics.otherProducts'),
            value: restCount,
            display: formatNumber(restCount, locale),
          },
        ]
      : []),
  ];

  return <BarList ariaLabel={t('metrics.topProducts')} labels={labels} data={data} />;
}
